import { ReactElement } from 'react';

import { AllessehArticleQueryResponse, useAllessehArticleQuery } from 'hooks/useAllessehArticleQuery';
import { AllessehContent, AllessehQuerySortKeyTypes } from 'hooks/useAllessehContentQuery';
import { SearchContentFilters } from '../search-content-filters/SearchContentFiltersContainer';
import SearchContentList from '../search-content-list/SearchContentList';

const MESSAGES = {
  NOT_FOUND: 'Article like this has not been found, please check the URL and try again.',
  ALREADY_USED: 'Article is already used on the page.',
  INVALID_URL: 'Article URL is invalid, please check the URL and try again.',
  WRONG_SECTION_TYPE: 'The article does not match the other filters in this search.'
};

function getMessage({
  data,
  isLoading,
  alreadyUsed,
  wrongSectionType,
  error
}: {
  data: AllessehArticleQueryResponse | undefined;
  isLoading: boolean;
  alreadyUsed: boolean;
  wrongSectionType: boolean;
  error: boolean;
}) {
  if (error) return MESSAGES.INVALID_URL;
  if (!data?.length && !isLoading) return MESSAGES.NOT_FOUND;
  if (alreadyUsed) return MESSAGES.ALREADY_USED;
  if (wrongSectionType) return MESSAGES.WRONG_SECTION_TYPE;
  return '';
}

function isURLValid(url: string) {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return false;
  } catch (e: unknown) {
    return true;
  }
}
interface SearchParams {
  search: string;
  filters: SearchContentFilters;
  sort: AllessehQuerySortKeyTypes;
}

export interface SearchContentByProps {
  searchParams: SearchParams;
  resultListClassName?: string;
  embargo?: boolean;
  renderContentCard: (content: AllessehContent, index: number, embargo?: boolean) => ReactElement;
  scratchpadItemIds?: string[];
}

const SearchByUrl = ({
  searchParams,
  resultListClassName,
  embargo,
  renderContentCard,
  scratchpadItemIds
}: SearchContentByProps) => {
  const error = isURLValid(searchParams.search);

  const {
    data,
    isLoading: isLoad,
    fetchStatus
  } = useAllessehArticleQuery(searchParams.search, {
    enabled: !!searchParams.search && !error
  });
  const isLoading = isLoad && fetchStatus !== 'idle';

  const items: AllessehContent[] = data ?? [];

  const alreadyUsedItems = items.filter((item) => searchParams.filters.excludedContentIds?.includes(item.data.id));
  const alreadyUsed = items.length > 0 && alreadyUsedItems.length === items.length;

  const wrongSectionTypeItems = items.filter(
    (item) => searchParams.filters.sectionType && searchParams.filters.sectionType !== item.data.attributes.section_type
  );
  const wrongSectionType = items.length > 0 && wrongSectionTypeItems.length === items.length;

  const message = getMessage({ data, isLoading, alreadyUsed, wrongSectionType, error });

  const includedContentItemsInSearch =
    wrongSectionType || alreadyUsed
      ? []
      : items.filter((item) => !alreadyUsedItems.includes(item) && !wrongSectionTypeItems.includes(item));

  return (
    <SearchContentList
      data-testid="search-content-list-by-url"
      excludedContentItemsInSearch={alreadyUsedItems}
      includedContentItemsInSearch={includedContentItemsInSearch}
      className={resultListClassName}
      isLoading={isLoading}
      customMessage={message}
      renderContentCard={renderContentCard}
      embargo={embargo}
      scratchpadItemIds={scratchpadItemIds}
    />
  );
};

export default SearchByUrl;
